import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";

import ProductsListContainer from "containers/ProductsListContainer.jsx";
import GroupedProductsContainer from "containers/GroupedProductsContainer.jsx";
import FilterPillContainer from "containers/FilterPillContainer.jsx";

import {
  getGroupByProducts,
  resetGroupedProducts
} from "actions/groupByProductsActions.js";

import { selectedGroupByFilterSelector } from "selectors/groupedProductsSelector.js";
import {
  fetchedDynamicFiltersSelector,
  appliedDynamicFiltersSelector,
  appliedCommonFiltersSelector
} from "selectors/filtersSelector.js";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";
import { buyplanIdSelector } from "selectors/buyplanIdSelector.js";

import { CATALOGUE_ORDER, PER_PAGE } from "constants.js";

const RangeContainer = () => {
  const { releaseId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const buyPlanState = queryParams.get("type");
  const buyplanId = useSelector(buyplanIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);

  const selectedGroupByFilter = useSelector(
    selectedGroupByFilterSelector
  ).toJS();
  const groupByFilter = selectedGroupByFilter.value;
  const appliedFilters = useSelector(appliedDynamicFiltersSelector);
  const appliedCommonFilters = useSelector(appliedCommonFiltersSelector);
  const fetchedFilters = useSelector(fetchedDynamicFiltersSelector);

  useEffect(() => {
    if (groupByFilter !== CATALOGUE_ORDER) {
      dispatch(
        getGroupByProducts(
          releaseId,
          PER_PAGE,
          0,
          true,
          buyPlanState,
          buyplanId,
          groupByFilter
        )
      );
      return () => {
        dispatch(resetGroupedProducts());
      };
    }
  }, [
    dispatch,
    releaseId,
    buyPlanState,
    buyplanId,
    appliedFilters,
    appliedCommonFilters,
    warehouseId,
    groupByFilter
  ]);

  // A release-level default_tell_story_by can name a grouping that carries no
  // data for this buyplan (or is missing entirely when the catalog query returns
  // no rows). Grouping is presentation only, so fall back to the ungrouped list
  // rather than crashing or rendering nothing.
  const availableGroups = groupByFilter && fetchedFilters.get(groupByFilter);
  const canGroup = groupByFilter !== CATALOGUE_ORDER && Boolean(availableGroups && availableGroups.size > 0);

  if (!canGroup) {
    return (
      <>
        <FilterPillContainer />
        <ProductsListContainer />
      </>
    );
  } else {
    if (groupByFilter) {
      let subcategoryFilters = [];

      // An applied selection - from the sidebar or restored from a campaign link -
      // narrows the sections to just the selected groups.
      if (
        appliedFilters &&
        appliedFilters.get(groupByFilter) &&
        appliedFilters.get(groupByFilter).size > 0
      ) {
        // Dedupe by name: the API emits one entry per division/collection pair,
        // so the same group name can legitimately appear many times.
        appliedFilters.get(groupByFilter).forEach(filterOption => {
          const isExistingFilterOption = subcategoryFilters.find(option => {
            return option.get("name") === filterOption.get("name");
          });

          isExistingFilterOption === undefined &&
            subcategoryFilters.push(filterOption);
        });
      } else {
        availableGroups.forEach(filterOption => {
          const isExistingFilterOption = subcategoryFilters.find(option => {
            return option.get("name") === filterOption.get("name");
          });

          isExistingFilterOption === undefined &&
            subcategoryFilters.push(filterOption);
        });
      }

      return subcategoryFilters.map((value, index) => {
        return (
          <GroupedProductsContainer
            key={index}
            groupName={value.get("name")}
            groupByFilter={groupByFilter}
          />
        );
      });
    }
  }
};

export default React.memo(RangeContainer);
